import React, { useState } from 'react';
import { Bell, Send } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const PushBroadcastTab = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Titre et message requis');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/push/broadcast', {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
      });
      setLastResult(data);
      toast.success(`Notification envoyée à ${data.sent}/${data.subscribers} abonné(s)`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(' · ') : (detail || 'Erreur d\'envoi');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="push-broadcast-tab">
      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-[#1F4D3F]" />
          <h2 className="font-serif text-xl font-semibold text-[#1F4D3F]">Notifications push</h2>
        </div>
        <p className="text-sm text-[#7A6F5C] mb-6">
          Envoyez une notification web push à tous les utilisateurs ayant activé les notifications depuis leur menu profil.
        </p>

        <div className="space-y-4 max-w-2xl">
          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">Titre</label>
            <Input
              data-testid="push-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Ex: Nouvelle promotion sur les composites"
            />
            <p className="text-xs text-[#7A6F5C] mt-1">{title.length}/120</p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">Message</label>
            <Textarea
              data-testid="push-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Ex: -15% sur toute la gamme Ivoclar jusqu'au dimanche !"
            />
            <p className="text-xs text-[#7A6F5C] mt-1">{body.length}/500</p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#2D2A26] mb-1.5 block">URL de destination (optionnel)</label>
            <Input
              data-testid="push-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/promotions ou /categorie/composites"
            />
            <p className="text-xs text-[#7A6F5C] mt-1">Chemin relatif vers lequel rediriger au clic sur la notification.</p>
          </div>

          <Button
            data-testid="push-send-btn"
            onClick={send}
            disabled={busy || !title.trim() || !body.trim()}
            className="bg-[#1F4D3F] hover:bg-[#163A2F] text-white">
            <Send className="w-4 h-4 mr-2" />
            {busy ? 'Envoi en cours…' : 'Envoyer à tous les abonnés'}
          </Button>
        </div>

        {lastResult && (
          <div className="mt-6 p-4 bg-[#F5F1EA] rounded-lg text-sm" data-testid="push-result">
            <div className="font-medium text-[#2D2A26] mb-2">Dernier envoi :</div>
            <ul className="space-y-1 text-[#5A5247]">
              <li>Abonnés totaux : <span className="font-medium">{lastResult.subscribers}</span></li>
              <li>Envoyés avec succès : <span className="font-medium text-[#1F4D3F]">{lastResult.sent}</span></li>
              <li>Échecs : <span className="font-medium text-[#C75B27]">{lastResult.failed}</span></li>
              {lastResult.cleaned > 0 && (
                <li>Abonnements expirés nettoyés : <span className="font-medium">{lastResult.cleaned}</span></li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushBroadcastTab;
